import React from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Check } from "lucide-react";
import { useI18n } from "../../i18n/I18nProvider";
import { API } from "../../lib/api";

const INTERESTS = [
  { id: "pos", color: "#E11D48" },
  { id: "kiosk", color: "#16A34A" },
  { id: "online_shop", color: "#0284C7" },
  { id: "qr_order", color: "#EA580C" },
  { id: "waiter_app", color: "#7C3AED" },
  { id: "payment", color: "#D97706" },
];

export default function DemoRequest() {
  const { t, locale } = useI18n();
  const [form, setForm] = React.useState({
    full_name: "",
    business_name: "",
    email: "",
    phone: "",
    business_type: "restaurant",
    locations: "",
    interested_in: ["pos"],
    message: "",
  });
  const [submitting, setSubmitting] = React.useState(false);
  const [done, setDone] = React.useState(false);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const toggleInterest = (id) =>
    setForm((s) => ({
      ...s,
      interested_in: s.interested_in.includes(id)
        ? s.interested_in.filter((x) => x !== id)
        : [...s.interested_in, id],
    }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/demo-request`, { ...form, locale });
      setDone(true);
      toast.success(t("demo.success_title"));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("common.error_submit"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="demo" data-testid="demo-request-section" className="py-24 sm:py-32 bg-zinc-50 scroll-mt-24">
      <div className="container-oj">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-500">{t("demo.eyebrow")}</div>
            <h2 className="font-display mt-4 text-balance text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] text-oj-ink">
              {t("demo.title")}
            </h2>
            <p className="mt-5 text-lg text-zinc-600 leading-relaxed max-w-md">{t("demo.sub")}</p>
            <ul className="mt-8 space-y-3">
              {(t("demo.bullets") || []).map((b) => (
                <li key={b} className="flex items-center gap-2 text-zinc-700">
                  <span className="size-5 rounded-full bg-oj-ink text-white grid place-items-center">
                    <Check className="size-3" />
                  </span>
                  {b}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-2xl bg-white border border-zinc-200 shadow-sm overflow-hidden">
              <div className="px-6 sm:px-8 py-4 border-b border-zinc-200 flex items-center justify-end">
                <div className="text-xs text-zinc-500">{t("demo.time_2min")}</div>
              </div>

              {done ? (
                <div data-testid="demo-success" className="p-10 sm:p-14 text-center">
                  <div className="mx-auto size-14 rounded-full bg-oj-kiosk text-white grid place-items-center">
                    <Check className="size-7" />
                  </div>
                  <h3 className="font-display mt-6 text-3xl font-black tracking-tighter text-oj-ink">{t("demo.success_title")}</h3>
                  <p className="mt-3 text-zinc-600 max-w-sm mx-auto">{t("demo.success_sub")}</p>
                  <button
                    onClick={() => { setDone(false); setForm((s) => ({ ...s, message: "" })); }}
                    className="mt-6 text-sm font-semibold text-oj-ink underline underline-offset-4"
                    data-testid="demo-reset"
                  >
                    {t("demo.success_again")}
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="p-6 sm:p-8 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="demo-form">
                  <Field label={t("demo.full_name")} required>
                    <input data-testid="demo-input-full-name" required value={form.full_name} onChange={set("full_name")} className={inputClass} placeholder="Anna Müller" />
                  </Field>
                  <Field label={t("demo.business_name")} required>
                    <input data-testid="demo-input-business-name" required value={form.business_name} onChange={set("business_name")} className={inputClass} placeholder="Café Pivot" />
                  </Field>
                  <Field label={t("demo.email")} required>
                    <input data-testid="demo-input-email" required type="email" value={form.email} onChange={set("email")} className={inputClass} placeholder="you@business.com" />
                  </Field>
                  <Field label={t("demo.phone")} required>
                    <input data-testid="demo-input-phone" required value={form.phone} onChange={set("phone")} className={inputClass} placeholder="+41 79 000 00 00" />
                  </Field>
                  <Field label={t("demo.business_type")} required>
                    <select data-testid="demo-input-business-type" required value={form.business_type} onChange={set("business_type")} className={inputClass}>
                      <option value="restaurant">{t("demo.bt_restaurant")}</option>
                      <option value="retail">{t("demo.bt_retail")}</option>
                      <option value="kiosk">{t("demo.bt_kiosk")}</option>
                      <option value="events">{t("demo.bt_events")}</option>
                      <option value="other">{t("demo.bt_other")}</option>
                    </select>
                  </Field>
                  <Field label={t("demo.locations")}>
                    <input data-testid="demo-input-locations" value={form.locations} onChange={set("locations")} className={inputClass} placeholder="1-3" />
                  </Field>

                  <div className="sm:col-span-2">
                    <label className="text-xs font-bold uppercase tracking-[0.18em] text-zinc-500">{t("demo.interested_in")}</label>
                    <div className="mt-3 flex flex-wrap gap-2" data-testid="demo-interests">
                      {INTERESTS.map((i) => {
                        const active = form.interested_in.includes(i.id);
                        return (
                          <button
                            type="button"
                            key={i.id}
                            data-testid={`demo-interest-${i.id}`}
                            onClick={() => toggleInterest(i.id)}
                            aria-pressed={active}
                            className={`text-xs font-semibold px-3 py-2 rounded-full border transition-all ${
                              active
                                ? "text-white border-transparent"
                                : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-900"
                            }`}
                            style={active ? { background: i.color } : {}}
                          >
                            {t(`demo_interests.${i.id}`)}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <Field label={t("demo.message")} className="sm:col-span-2">
                    <textarea data-testid="demo-input-message" value={form.message} onChange={set("message")} rows={3} className={inputClass + " resize-none"} placeholder="..." />
                  </Field>

                  <div className="sm:col-span-2 flex items-center justify-between gap-4 pt-2">
                    <div className="text-xs text-zinc-500">{t("demo.consent")}</div>
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="demo-submit-btn"
                      className="inline-flex items-center gap-1.5 bg-oj-ink text-white font-semibold px-6 py-3.5 rounded-md hover:bg-zinc-800 transition-colors disabled:opacity-60"
                    >
                      {submitting ? t("demo.sending") : t("demo.submit")} <ArrowUpRight className="size-4" />
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const inputClass = "w-full bg-zinc-50 border border-zinc-200 rounded-md px-4 py-3 text-sm text-oj-ink placeholder:text-zinc-400 focus:outline-none focus:border-oj-ink focus:ring-2 focus:ring-oj-ink/10 transition";

function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-xs font-bold uppercase tracking-[0.18em] text-zinc-500">
        {label} {required && <span className="text-oj-pos">*</span>}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
